import type { CompetitorNearbyPlaceDto } from '@up2stay/up2promo-api-client';
import { Button, Card, cn } from '@up2stay/up2promo-ui';
import { Plus, Radar, Search } from 'lucide-react';

import { focusCompetitorSearch } from '@/core/components/reputation/competitors/competitor-tracked-card';
import type { Dictionary } from '@/core/providers/dictionary-provider';

type Dict = Dictionary['reputation']['competitorsPage']['suggestions'];

/** How many nearby businesses to propose. */
export const SUGGESTION_LIMIT = 5;

/** Best candidates first: rated and well reviewed businesses are the real rivals. */
export function pickSuggestions(
  places: CompetitorNearbyPlaceDto[],
): CompetitorNearbyPlaceDto[] {
  return places
    .filter((place) => !place.alreadyTracked)
    .sort(
      (a, b) =>
        Number(b.rating != null) - Number(a.rating != null) ||
        (b.reviewCount ?? 0) - (a.reviewCount ?? 0),
    )
    .slice(0, SUGGESTION_LIMIT);
}

/** One-click rows for nearby businesses (used in the slot card and under the tracked chips). */
export function SuggestionRows({
  suggestions,
  isAdding,
  dict,
  locale,
  onTrack,
  onFocus,
}: {
  suggestions: CompetitorNearbyPlaceDto[];
  isAdding: boolean;
  dict: Dict;
  locale: string;
  onTrack: (place: CompetitorNearbyPlaceDto) => void;
  /** Highlight the place on the map. */
  onFocus: (place: CompetitorNearbyPlaceDto) => void;
}) {
  return (
    <ul className="flex flex-col">
      {suggestions.map((place) => (
        <li
          key={place.placeId}
          className="flex items-center gap-3 border-b py-2.5 last:border-b-0"
        >
          <button
            type="button"
            className="min-w-0 flex-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={() => onFocus(place)}
          >
            <span className="block truncate text-[13.5px] font-semibold">
              {place.name}
            </span>
            <span className="text-muted-foreground block truncate text-xs">
              {place.rating != null
                ? `★ ${place.rating.toFixed(1)}`
                : dict.noRating}
              {place.reviewCount != null
                ? ` · ${dict.reviews.replace('{count}', place.reviewCount.toLocaleString(locale))}`
                : ''}
            </span>
          </button>
          <Button
            variant="ghostAccent"
            size="sm"
            disabled={isAdding}
            onClick={() => onTrack(place)}
          >
            <Plus className="mr-1 size-3.5" aria-hidden />
            {dict.track}
          </Button>
        </li>
      ))}
    </ul>
  );
}

/**
 * Shown in the "Where you stand" slot while nothing is tracked: proposes nearby
 * businesses (one click to track). With none found, explains how to add one;
 
 */
export function CompetitorSuggestionsCard({
  suggestions,
  isAdding,
  dict,
  locale,
  onTrack,
  onFocus,
  className,
}: {
  suggestions: CompetitorNearbyPlaceDto[];
  isAdding: boolean;
  dict: Dict;
  locale: string;
  onTrack: (place: CompetitorNearbyPlaceDto) => void;
  onFocus: (place: CompetitorNearbyPlaceDto) => void;
  className?: string;
}) {
  if (suggestions.length === 0) {
    return (
      <Card
        className={cn(
          'flex min-w-0 flex-col items-center justify-center gap-3 p-6 text-center sm:p-8',
          className,
        )}
      >
        <span className="flex size-12 items-center justify-center rounded-2xl bg-muted text-muted-foreground">
          <Radar className="size-5" aria-hidden />
        </span>
        <div className="max-w-sm">
          <p className="text-[15.5px] font-bold">{dict.noneTitle}</p>
          <p className="text-muted-foreground mt-1 text-[13.5px] leading-relaxed text-pretty">
            {dict.noneBody}
          </p>
        </div>
        <div className="flex flex-wrap justify-center gap-2">
          <Button
            variant="ghostAccent"
            size="sm"
            onClick={focusCompetitorSearch}
          >
            <Search className="mr-1.5 size-4" aria-hidden />
            {dict.searchCta}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className={cn('flex min-w-0 flex-col gap-3.5 p-5 sm:p-6', className)}>
      <div>
        <p className="text-[15.5px] font-bold">{dict.title}</p>
        <p className="text-muted-foreground mt-0.5 text-[12.5px]">
          {dict.intro}
        </p>
      </div>
      <SuggestionRows
        suggestions={suggestions}
        isAdding={isAdding}
        dict={dict}
        locale={locale}
        onTrack={onTrack}
        onFocus={onFocus}
      />
      <p className="text-muted-foreground mt-auto text-xs">{dict.footer}</p>
    </Card>
  );
}
